import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Description as DescriptionIcon } from '@mui/icons-material';

interface DefaultLoadingSheetTemplateProps {
  onApplyDefault: (template: string) => void;
  companyName?: string;
  brandingName?: string;
  brandingLogoUrl?: string;
  regCountry?: string;
}

export const DEFAULT_LOADING_SHEET_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Loading Sheet - {{bookingId}}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 40px;
      background-color: #ffffff;
      color: #333;
    }
    
    .header {
      text-align: center;
      margin-bottom: 40px;
      border-bottom: 3px solid #2196F3;
      padding-bottom: 20px;
    }
    
    .header h1 {
      color: #2196F3;
      font-size: 32px;
      margin-bottom: 10px;
    }
    
    .header .booking-id {
      font-size: 16px;
      color: #666;
      font-weight: 500;
    }
    
    .section {
      margin: 30px 0;
    }
    
    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: #2196F3;
      margin-bottom: 15px;
      border-left: 4px solid #2196F3;
      padding-left: 10px;
    }
    
    .info-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      background-color: #ffffff;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .info-table th,
    .info-table td {
      border: 1px solid #e0e0e0;
      padding: 14px 16px;
      text-align: left;
    }
    
    .info-table th {
      background-color: #f5f5f5;
      font-weight: 600;
      color: #333;
      width: 30%;
    }
    
    .info-table td {
      background-color: #ffffff;
    }
    
    .info-table tr:hover td {
      background-color: #f9f9f9;
    }
    
    .route-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin: 20px 0;
    }
    
    .route-card {
      padding: 20px;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      background-color: #fafafa;
    }
    
    .route-card h3 {
      color: #2196F3;
      font-size: 16px;
      margin-bottom: 12px;
    }
    
    .route-card p {
      margin: 8px 0;
      line-height: 1.6;
    }
    
    .route-card .label {
      font-weight: 600;
      color: #666;
      margin-right: 8px;
    }
    
    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 2px solid #e0e0e0;
      text-align: center;
      color: #999;
      font-size: 12px;
    }
    
    @media print {
      body {
        padding: 20px;
      }
      
      .info-table {
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    {{#brandingLogoUrl}}
    <img src="{{brandingLogoUrl}}" alt="{{brandingName}}" style="max-height: 60px; margin-bottom: 10px;" />
    {{/brandingLogoUrl}}
    <h1>📋 Loading Sheet</h1>
    {{#brandingName}}
    <p style="font-size: 18px; color: #2196F3; font-weight: 600; margin: 5px 0;">{{brandingName}}</p>
    {{/brandingName}}
    {{#companyAddress}}
    <p style="font-size: 13px; color: #666; margin: 3px 0;">{{companyAddress}}</p>
    {{/companyAddress}}
    <p class="booking-id">Booking Reference: <strong>{{bookingId}}</strong></p>
  </div>
  
  <div class="section">
    <h2 class="section-title">Vehicle & Driver Information</h2>
    <table class="info-table">
      <tr>
        <th>🚛 Vehicle Plate</th>
        <td>{{vehiclePlate}}</td>
      </tr>
      <tr>
        <th>👤 Driver Name</th>
        <td>{{driverName}}</td>
      </tr>
      <tr>
        <th>📧 Driver Email</th>
        <td>{{driverEmail}}</td>
      </tr>
    </table>
  </div>
  
  <div class="section">
    <h2 class="section-title">Route Details</h2>
    <div class="route-section">
      <div class="route-card">
        <h3>📍 Pickup Location</h3>
        <p><span class="label">Address:</span>{{startAddress}}</p>
        <p><span class="label">Time:</span>{{startTime}}</p>
      </div>
      <div class="route-card">
        <h3>🎯 Delivery Location</h3>
        <p><span class="label">Address:</span>{{endAddress}}</p>
        <p><span class="label">Time:</span>{{endTime}}</p>
      </div>
    </div>
  </div>
  
  <div class="section">
    <h2 class="section-title">Booking Summary</h2>
    <table class="info-table">
      <tr>
        <th>📏 Distance</th>
        <td>{{distance}}</td>
      </tr>
      <tr>
        <th>💰 Price</th>
        <td>{{price}}</td>
      </tr>
      <tr>
        <th>✅ Status</th>
        <td>{{status}}</td>
      </tr>
    </table>
  </div>
  
  <div class="footer">
    <p>This loading sheet was automatically generated by your transport management system.</p>
    {{#brandingName}}
    <p><strong>{{brandingName}}</strong>{{#companyAddress}} • {{companyAddress}}{{/companyAddress}}</p>
    {{/brandingName}}
    <p>For questions or concerns, please contact: {{notificationEmail}}</p>
  </div>
</body>
</html>`;

const DefaultLoadingSheetTemplate: React.FC<DefaultLoadingSheetTemplateProps> = ({ onApplyDefault, companyName, brandingName, brandingLogoUrl, regCountry }) => {
  const { t } = useTranslation();

  const handleApplyDefault = () => {
    let template = DEFAULT_LOADING_SHEET_TEMPLATE;
    
    // Replace tenant-specific placeholders with actual values or remove conditional blocks
    if (brandingLogoUrl) {
      template = template.replace(/\{\{#brandingLogoUrl\}\}/g, '');
      template = template.replace(/\{\{\/brandingLogoUrl\}\}/g, '');
      template = template.replace(/\{\{brandingLogoUrl\}\}/g, brandingLogoUrl);
      if (brandingName) {
        template = template.replace(/\{\{brandingName\}\}/g, brandingName);
      }
    } else {
      // Remove the logo section if no logo URL is provided
      template = template.replace(/\{\{#brandingLogoUrl\}\}[\s\S]*?\{\{\/brandingLogoUrl\}\}/g, '');
    }
    
    if (brandingName) {
      template = template.replace(/\{\{#brandingName\}\}/g, '');
      template = template.replace(/\{\{\/brandingName\}\}/g, '');
      template = template.replace(/\{\{brandingName\}\}/g, brandingName);
    } else {
      template = template.replace(/\{\{#brandingName\}\}[\s\S]*?\{\{\/brandingName\}\}/g, '');
    }
    
    // Create company address string with country if available
    const companyAddress = regCountry ? `${companyName || ''}, ${regCountry}`.trim().replace(/^,\s*/, '') : companyName || '';
    if (companyAddress) {
      template = template.replace(/\{\{#companyAddress\}\}/g, '');
      template = template.replace(/\{\{\/companyAddress\}\}/g, '');
      template = template.replace(/\{\{companyAddress\}\}/g, companyAddress);
    } else {
      template = template.replace(/\{\{#companyAddress\}\}[\s\S]*?\{\{\/companyAddress\}\}/g, '');
    }
    
    // Note: notificationEmail will be handled at runtime when generating actual documents
    // as it's part of the tenant data that can be accessed during PDF generation
    
    onApplyDefault(template);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'border.default',
        backgroundColor: 'background.lightGrey',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionIcon />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('Default Loading Sheet Template')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('A professionally designed template with modern styling and organized sections. Includes vehicle info, driver details, route information, and booking summary.')}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('Features:')}
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Clean, professional layout with modern styling')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Organized sections for vehicle, driver, route, and booking details')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Print-friendly design')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Responsive layout with icons for better readability')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Includes company branding (logo, branding name, company location with country)')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('All standard placeholders: {{bookingId}}, {{vehiclePlate}}, {{driverName}}, {{driverEmail}}, {{startAddress}}, {{endAddress}}, {{startTime}}, {{endTime}}, {{price}}, {{distance}}, {{status}}, {{notificationEmail}}, {{customerDomain}}, {{externalOfferId}}')}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={handleApplyDefault}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {t('Apply Default Template')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default DefaultLoadingSheetTemplate;

export const ORDEM_TRANSPORTE_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Ordem de Transporte - {{bookingId}}</title>
  <link href="https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Carlito', Calibri, Arial, sans-serif;
      font-size: 12px;
      color: #222;
      background: #fff;
    }

    @page { size: A4 portrait; margin: 12mm 15mm 22mm; }

    .page {
      width: 100%;
      padding: 0;
    }

    .company-header {
      display: flex;
      align-items: flex-start;
      gap: 15px;
      margin-bottom: 18px;
    }

    .company-logo { max-height: 120px; max-width: 200px; }

    .company-info {
      font-size: 11px;
      line-height: 1.7;
    }

    .doc-title {
      text-align: center;
      font-size: 20px;
      font-weight: bold;
      color: #1565C0;
      letter-spacing: 2px;
      margin: 15px 0;
    }

    .meta-table {
      width: 100%;
      margin-bottom: 15px;
      font-size: 12px;
      border-collapse: collapse;
    }

    .meta-table td { padding: 3px 8px 3px 0; }
    .meta-label { color: #444; }
    .meta-value { font-weight: 500; }

    .section-label {
      font-size: 13px;
      font-weight: bold;
      margin-bottom: 6px;
    }

    .section-box {
      border: 1.5px solid #444;
      border-radius: 8px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }

    .row {
      display: flex;
      align-items: baseline;
      margin-bottom: 5px;
      gap: 6px;
      flex-wrap: wrap;
    }

    .lbl { color: #444; white-space: nowrap; }
    .val:not(:empty) { text-decoration: underline; text-decoration-color: #ddd; text-decoration-thickness: 1px; }
    .spacer { flex: 1; }

    .page-footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: #1565C0;
      color: #fff;
      display: flex;
    }

    .footer-col {
      flex: 1;
      padding: 8px 10px;
      font-size: 9.5px;
      line-height: 1.6;
      text-align: center;
      border-right: 1px solid rgba(255,255,255,0.3);
    }

    .footer-col:last-child { border-right: none; }

    .page-break { page-break-after: always; }

    .req-title {
      text-align: center;
      font-size: 13px;
      font-weight: bold;
      text-decoration: underline;
      color: #CC0000;
      margin: 16px 0 12px;
    }

    .req-list {
      font-size: 10px;
      line-height: 1.45;
      margin-bottom: 12px;
      padding-left: 16px;
    }

    .req-list li { margin-bottom: 3px; }

    .add-req-intro {
      font-size: 10px;
      font-weight: 600;
      margin-bottom: 6px;
      line-height: 1.45;
    }

    .add-req-list {
      font-size: 10px;
      line-height: 1.45;
      list-style: none;
      padding-left: 0;
    }

    .add-req-list li { margin-bottom: 2px; }
  </style>
</head>
<body>

<!-- PAGE 1 -->
<div class="page page-break">
  <div class="page-content">

    <!-- Company Header -->
    <div class="company-header">
      <img src="data:image/png;base64,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" alt="Suprema Medida LDA" class="company-logo" />
      <div class="company-info">
        <strong>Suprema Medida LDA</strong><br>
        Av.da República, 3000, Porta B, 3.Andar<br>
        2649-517 Alcabideche<br>
        Tel.+351 211 555 119<br>
        Email: j.goncalves@septlog.pt<br>
        NIF: 514952016
      </div>
    </div>

    <!-- Document Title -->
    <div class="doc-title">ORDEM DE TRANSPORTE</div>

    <!-- Meta row: ID / Tractor / Date / Trailer -->
    <table class="meta-table">
      <tr>
        <td><span class="meta-label">ID</span></td>
        <td><span class="meta-value">{{bookingId}}</span></td>
        <td style="padding-left:40px"><span class="meta-label">Tractor:</span></td>
        <td><span class="meta-value">{{vehiclePlate}}</span></td>
      </tr>
      <tr>
        <td><span class="meta-label">Data:</span></td>
        <td><span class="meta-value">{{now}}</span></td>
        <td style="padding-left:40px"><span class="meta-label">Reboque:</span></td>
        <td><span class="meta-value">{{trailerPlate}}</span></td>
      </tr>
    </table>

    {{loadingBlocks}}

  </div><!-- /.page-content -->
</div>

<!-- PAGE 2 – Requirements -->
<div class="page">
  <div class="page-content">

    <img src="data:image/png;base64,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" alt="Suprema Medida LDA" style="max-height:90px; margin-bottom:15px;" />

    <div class="req-title">Requisitos de transporte:</div>
    <ol class="req-list">
      <li>Assegurar que o veículo está suficientemente limpo, de modo a evitar contaminações e a garantir a segurança dos produtos. Caso não disponha de meios que possam garantir o estipulado, deverá comunicar de imediato.</li>
      <li>Verificar a segurança, estiva e acondicionamento da mercadoria, de modo a garantir o seu bom estado durante todo o transporte;</li>
      <li>O motorista deve ter uma atitude responsável, cordial e permanecer sempre contactável;</li>
      <li>Em caso de avaria/emergência/reservas/atrasos, acidentes rodoviários ou algum tipo de incidências com a mercadoria, entrada ilegal de emigrantes, roubo ou tentativa de roubo de mercadorias, abertura indevida de portas do semi-reboque com consequente violação de selo, <strong>deve informar imediatamente a Suprema Medida LDA - a quem lhe atribuiu a carga.</strong></li>
      <li>A mercadoria carregada deverá ser sempre devidamente conferida e ser correspondente à declarada no CMR (quantidades e pesos);</li>
      <li>Sempre que lhe seja permitido, deve observar o processo de carga do camião, <strong>escrevendo reservas adequadas</strong> à situação no campo 18 do CMR. Caso não seja permitido assistir, deve <strong>anotar reservas nesse sentido</strong>;</li>
      <li>Os documentos de transporte, nomeadamente o CMR, devem ser preenchidos e apenas assinados na presença do cliente no final das operações de carga;</li>
      <li>Assegurar-se que o expedidor e /ou destinatário assinam e carimbam o CMR nos campos designados para o efeito;</li>
      <li>Em caso de passagem em alfândega, é obrigatório utilizar o terminal de alfândega indicado pela <strong>Suprema Medida LDA.</strong></li>
      <li><strong>A descarga da mercadoria deverá ser efetuada apenas no local indicado no CMR e coincidente com as ordens de carga.</strong> Caso lhe indiquem uma morada diferente, deve comunicar de imediato a Suprema Medida LDA, antes de avançar;</li>
      <li>Caso sejam anotadas reservas à descarga por parte do destinatário, devido a deficiência de embalagem, faltas ou outras, <strong>deverá de imediato informar</strong> a Suprema Medida LDA e <strong>aguardar no local, até novas instruções</strong>;</li>
      <li><strong>Após cada descarga, enviar de imediato</strong>, e por via digital, <strong>o CMR assinado e carimbado</strong>.</li>
      <li>Respeitar prazos de carga e descarga.</li>
      <li>Sempre antes de iniciar a viagem, tirar e enviar pelo WhatsApp uma foto do marcador de combustível e <strong>após o carregamento tirar uma foto da carga com a lona aberta.</strong></li>
    </ol>

    <div class="req-title">Requisitos adicionais:</div>
    <p class="add-req-intro">Cumprir na integra os requisitos impostos pelos clientes da Suprema Medida LDA, seguindo as instruções gerais de segurança em cada local de carga ou descarga, nomeadamente:</p>
    <ul class="add-req-list">
      <li>- O uso de equipamento de proteção individual (EPI) nas instalações (botas de segurança, colete refletor, capacete, luvas, óculos, ou outros que sejam exigidos pelo expedidor);</li>
      <li>- Respeitar os limites de velocidade impostos e cumprir todas as normas do código de estrada em vigor</li>
      <li>- Não aceder às instalações sem autorização do pessoal local e utilizar unicamente os acessos definidos para circular nos recintos;</li>
      <li>- Respeitar o acesso proibido a pessoas não autorizadas, crianças e animais;</li>
      <li>- Respeitar as informações afixadas e as proibições de fumar;</li>
      <li>- Respeitar a sinalética de circulação existente e a limpeza do local</li>
      <li>- É proibido o consumo de álcool e entopercentes antes e durante a condução</li>
      <li>- É proibida a utilização do telemóvel e qualquer outro aparelho durante a condução</li>
      <li>- O motorista é responsável pelo cumprimento do horário de trabalho, respeitando os limites de hora de condução, pausa e repouso.</li>
    </ul>

  </div><!-- /.page-content -->
</div>

<!-- Shared footer – fixed at bottom, prints on every page -->
<div class="page-footer">
  <div class="footer-col">Avenida da Republica, 3000, porta B<br>2649-517 Alcabideche<br>Lisboa, Portugal</div>
  <div class="footer-col">Bergensesteenweg, 421/13<br>1600 Sint-Pieters-Leeuw<br>Bruxelles, Belgique</div>
  <div class="footer-col">j.araujo@septlog.pt<br>www.septlog.pt<br>Tel – PT: +351 211 555 119<br>Tel - BE: +32 477075858</div>
</div>

</body>
</html>`;

interface OrdemTransporteTemplateProps {
  onApplyDefault: (template: string) => void;
}

export const OrdemTransporteTemplate: React.FC<OrdemTransporteTemplateProps> = ({ onApplyDefault }) => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'border.default',
        backgroundColor: 'background.lightGrey',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#1565C0',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionIcon />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('Ordem de Transporte (PT)')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Portuguese-style transport order with two pages: loading/delivery details on page 1 and transport requirements on page 2.')}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('Features:')}
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('2-page layout: order on page 1, transport requirements on page 2')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Sections: Local de Carga, Destino 1° Descarga, Destino 2° Descarga')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Blue branded footer on each page')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('Standard placeholders plus: {{trailerPlate}}, {{loadingDate}}, {{loadingTime}}, {{loadingLocality}}, {{loadingName}}, {{loadingPostalCode}}, {{cargoType}}, {{deliveryDate}}, {{deliveryLocality}}, {{deliveryName}}, {{deliveryPostalCode}}')}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={() => onApplyDefault(ORDEM_TRANSPORTE_TEMPLATE)}
            sx={{
              backgroundColor: '#1565C0',
              color: 'white',
              '&:hover': { backgroundColor: '#0d47a1' },
            }}
          >
            {t('Apply Ordem de Transporte Template')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
